import React, { Component } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Popup } from '@progress/kendo-react-popup';
import { ShowChart } from 'styled-icons/material';
import dayjs from 'dayjs';
import { DateRangePicker } from '@progress/kendo-react-dateinputs';
import { LocalizationProvider, IntlProvider, loadMessages } from '@progress/kendo-react-intl';
import { DotsVerticalRounded } from 'styled-icons/boxicons-regular';
import BarLoader from 'react-spinners/BarLoader';
import Grid from '@material-ui/core/Grid';
import { manageVacancy } from '../../../redux/actions/_admin_actions';
import database from '../../../model/firebase/firebase';
import DownloadCompaniesReport from './excel';
import { am4charts, am4core } from '../../../system/utils/amcharts';
import esMessages from '../../../system/utils/messagesSpanishKendo/es.json';
import { palette } from '../../../system/styles/styles';
import tree from '../../../assets/img_2.png';

const isBetween = require('dayjs/plugin/isBetween');

dayjs.extend(isBetween);

function CompaniesDashboard(props) {
  const { className } = props;

  const { platform } = useSelector((state) => state);
  const { companies } = useSelector((state) => state);
  const vacancies = useSelector((state) => state.vacancies);

  const language = { locale: 'es' };
  loadMessages(esMessages, 'es'); // Le decimos que mensajes queremos por idioma
  const maxDate = new Date();

  const chart = React.useRef(null);
  const contentRef = React.useRef();
  const blurTimeoutRef = React.useRef();

  const [state, setState] = React.useState({
    vacancies: [],
    message: false,
    allTime: true,
    activeCompanies: [],
    promVacancies: [],
    totalVacancies: 0,
    renderGraph: false,
    data: [],
    campusVacancies: [],
    topFive: [],
    campusCompanies: [],
    maxDate: null,
    minDate: null,
    currentVacanciesCounter: [],
    daysCount: 0,
    campus: 'all',
    openMenu: false,
    open: false,
  });

  const anchorEl = React.useRef();

  const handleCloseMenu = () => {
    setState((prev) => ({
      ...prev,
      anchorEl: null,
    }));
  };

  const handleClick = (event) => {
    setState((prev) => ({
      ...prev,
      open: !state.open,
    }));
  };

  const updateAnalytics = async (e) => {
    const value = e;
    setState((prev) => ({
      ...prev,
      renderGraph: false,
      campus: value,
      vacancies: [],
      campusCompanies: [],
      activeCompanies: [],
      currentVacanciesCounter: [],
      data: [0, 0, 0],
    }));
    const newVacancies = vacancies.map((acc, element) => {
      if (element.Permission === 'accept' && element.ATS === false) {
        if (state.allTime) {
          acc.push(element);
          return acc;
        }
        const evalDate = dayjs(element.postedFrom).isBetween(state.minDate, state.maxDate);
        if (evalDate) {
          acc.push(element);
          return acc;
        }
      }
      return acc;
    }, []);
    setState((prevState) => ({
      ...prevState,
      vacancies: newVacancies,
    }));
    const data = await getAnalytics(value);
    if (value === 'all') {
      const topFive = await database
        .collection(`${platform.platformName}`)
        .doc('data')
        .collection('companies')
        .where('active', '==', true)
        .orderBy('vacanciesCount', 'desc')
        .limit(5)
        .get();
      setState((prev) => ({ ...prev, topFive: topFive.docs }));
    }
    let totalCompanies;
    let active;
    let prom;
    let totalVacancies;
    totalCompanies = data.campusCompanies.length;
    active = data.activeCompanies.length;
    totalVacancies = state.vacancies.length;
    prom = totalCompanies === 0 ? 0 : parseInt(totalVacancies / totalCompanies);
    setState((prev) => ({
      ...prev,
      data: [totalCompanies, active, prom],
      renderGraph: true,
    }));
  };

  const changedRangeInputs = (e) => {
    const { end, start } = e.value;
    if (start) {
      setState((prev) => ({
        ...prev,
        minDate: start,
        allTime: false,
      }));
    }
    if (end) {
      setState((prev) => ({
        ...prev,
        maxDate: end,
        allTime: false,
      }));
    }
    if (start !== null && end !== null) {
      updateAnalytics(state.campus);
    }
  };

  const onOpen = (e) => {
    contentRef.current.focus();
  };

  const onFocus = () => {
    // the user is still inside the content
    clearTimeout(blurTimeoutRef.current);
  };

  const onBlurTimeout = () => {
    // the user is now outside the popup
    setState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const onBlur = () => {
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(onBlurTimeout, 200);
  };

  const getAnalytics = async (campus) => {
    if (campus === 'all') {
      const newCompany = companies.reduce(
        (acc, element) => {
          const evalDateReg = dayjs(element.authTime).isBetween(state.minDate, state.maxDate);
          if (element.active) {
            if (state.allTime) {
              acc.campusCompanies.push(element);
            } else if (evalDateReg) {
              acc.campusCompanies.push(element);
            }
          }
          if (element.lastLogin !== undefined) {
            if (element.active) {
              if (element.lastLogin !== '') {
                if (state.allTime) {
                  acc.activeCompanies.push(element);
                } else if (evalDateReg) {
                  acc.activeCompanies.push(element);
                }
              }
            }
          }
          return acc;
        },
        {
          campusCompanies: [],
          activeCompanies: [],
        }
      );
      setState((prevState) => ({
        ...prevState,
        activeCompanies: newCompany.activeCompanies,
        campusCompanies: newCompany.campusCompanies,
      }));
      return {
        activeCompanies: newCompany.activeCompanies,
        campusCompanies: newCompany.campusCompanies,
      };
    } else {
      const companies = (
        await database
          .collection(`${platform.platformName}`)
          .doc('data')
          .collection('companies')
          .where('campusSegment', '==', campus)
          .where('active', '==', true)
          .get()
      ).docs.map((doc) => doc.data());
      const topFive = await database
        .collection(`${platform.platformName}`)
        .doc('data')
        .collection('companies')
        .where('campusSegment', '==', campus)
        .where('active', '==', true)
        .orderBy('vacanciesCount', 'desc')
        .limit(5)
        .get();
      setState((prev) => ({ ...prev, topFive: topFive.docs }));
      const newCompanies = companies.reduce((acc, element) => {
        if (element.lastLogin !== undefined) {
          if (state.allTime) {
            acc.push(element);
            return acc;
          }
          const evalDate = dayjs(element.lastLogin).isBetween(state.minDate, state.maxDate);
          if (element.lastLogin !== '' && evalDate) {
            acc.push(element);
            return acc;
          }
        }
        return acc;
      }, []);
      setState((prevState) => ({
        ...prevState,
        campusCompanies: companies,
        activeCompanies: newCompanies,
      }));
      return {
        campusCompanies: companies,
        activeCompanies: newCompanies,
      };
    }
  };

  const effect = async () => {
    window.scrollTo(0, 0);
    await setState((prev) => ({
      ...prev,
      totalCompanies: platform.companiesCount,
    }));
    const data = await getAnalytics('all');
    const newVacancies = vacancies.reduce((acc, element) => {
      if (element.Permission === 'accept' && element.ATS === false) {
        acc.push(element);
      }
      return acc;
    }, []);
    setState((prev) => ({
      ...prev,
      vacancies: newVacancies,
    }));
    let totalCompanies;
    let active;
    let prom;
    let totalVacancies;
    const topFive = await database
      .collection(`${platform.platformName}`)
      .doc('data')
      .collection('companies')
      .where('active', '==', true)
      .orderBy('vacanciesCount', 'desc')
      .limit(5)
      .get();
    setState((prev) => ({
      ...prev,
      topFive: topFive.docs,
    }));
    totalCompanies = data.campusCompanies.length;
    active = data.activeCompanies.length;
    totalVacancies = newVacancies.length;
    prom = totalCompanies === 0 ? 0 : parseInt(totalVacancies / totalCompanies);
    setState((prev) => ({
      ...prev,
      data: [totalCompanies, active, prom],
      renderGraph: true,
    }));
  };

  React.useEffect(() => {
    effect();
  }, []);

  React.useLayoutEffect(() => {
    const x = am4core.create('barChart', am4charts.XYChart);
    x.data = [
      {
        category: 'Total',
        value: state.data[0] || 0,
      },
      {
        category: 'Activas',
        value: state.data[1] || 0,
      },
      {
        category: 'Vacantes / empresa',
        value: state.data[2] || 0,
      },
    ];

    const categoryAxis = x.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'category';
    categoryAxis.title.text = '';
    categoryAxis.renderer.minGridDistance = 40;
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;
    categoryAxis.renderer.minGridDistance = 30;

    const valueAxis = x.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = '';

    // Otra serie
    const series4 = x.series.push(new am4charts.ColumnSeries());
    series4.name = '{category}';
    series4.stroke = am4core.color(`${platform.adminColor ? platform.adminColor : '#0f3f44'}`);
    series4.strokeWidth = 3;
    series4.dataFields.valueY = 'value';
    series4.tooltipText = `{valueX}: [bold]{valueY}[/]`;
    series4.dataFields.categoryX = 'category';
    series4.smoothing = 'monotoneX';
    series4.columns.template.fill = am4core.color(`${platform.adminColor ? platform.adminColor : '#0f3f44'}`); // fill
    series4.columns.template.width = am4core.percent(60);

    // Cambia el color del background del tootip
    series4.tooltip.getFillFromObject = false;
    series4.tooltip.background.fill = am4core.color('#FFF');

    // Set text color
    series4.tooltip.label.fill = am4core.color('#000');

    // const scrollbarX = new am4charts.XYChartScrollbar();
    // scrollbarX.series.push(series4);
    // scrollbarX.start = 0
    // scrollbarX.end = .08
    // x.scrollbarX = scrollbarX;

    // Add cursor
    x.cursor = new am4charts.XYCursor();

    chart.current = x;

    return () => {
      x.dispose();
    };
  }, [state.renderGraph]);

  if (!state.renderGraph)
    return (
      <div
        className={className}
        style={{
          height: '500px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '30px 46px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#fff',
        }}>
        <BarLoader sizeUnit="px" size={250} color={palette.blue} />
      </div>
    );

  return (
    <Grid container spacing={2} className={className}>
      <Grid item lg={7} className="dash-card">
        <div className="card-heading">
          <h2 className="card-title">Empresas - General</h2>
          <div className="dashboard-date-container">
            <DateRangePicker
              format="dd-MMM-yyyy"
              value={{ start: state.minDate, end: state.maxDate }}
              onChange={changedRangeInputs}
              max={maxDate}
            />
          </div>
          <button type="button" onClick={handleClick} ref={anchorEl} style={{ background: 'transparent' }}>
            <DotsVerticalRounded size={20} color="#232323" />
          </button>
          <Popup anchor={anchorEl.current} show={state.open} onOpen={onOpen} popupClass="popup-content">
            <div ref={contentRef} tabIndex={0} onFocus={onFocus} onBlur={onBlur}>
              <button type="button" onClick={handleCloseMenu}>
                <DownloadCompaniesReport
                  filename={`ResumenCampus-${state.campus}`}
                  companiesDataSet={state.campusCompanies}
                />
              </button>
            </div>
          </Popup>
        </div>
        <div className="analytics-container">
          <div className="dashboard-card">
            <div className="graph-left-container">
              {state.renderGraph ? (
                <div className="a-main-card">
                  {platform.platformCampus.length === 0 ? null : (
                    <div className="graph-filter-container">
                      <label className="graph-filter-label">Filtrar por campus / sede</label>
                      <select
                        className="graph-filter-select"
                        value={state.campus}
                        onChange={(e) => {
                          updateAnalytics(e.target.value);
                        }}>
                        <option value="all">General</option>
                        {platform.platformCampus.map((a) => {
                          return (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  <div className="graph-container" id="barChart" />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Grid>
      <Grid item lg={4} className="dash-card">
        <div className="card-heading">
          <h2 className="card-title">Empresas que más publican</h2>
        </div>
        <div>
          {state.renderGraph
            ? state.topFive.map((v) => {
                return (
                  <div key={v.id} className="top-five-item">
                    <div>
                      <p>Empresa</p>
                      <h4 className="top-five-text">{`${v.data().companyName}`}</h4>
                    </div>
                    <h3 className="top-five-count">{v.data().vacanciesCount}</h3>
                  </div>
                );
              })
            : null}
        </div>
        <img
          src="https://lapieza-unis-admin-test.s3.us-west-1.amazonaws.com/static/media/img_2.d9abe9d2.png"
          className="img-b"
          alt=""
        />
      </Grid>
    </Grid>
  );
}

export default styled(CompaniesDashboard)`
  width: 100%;
  font-family: 'Sofia Pro', sans-serif;
  padding: 0 32px;
  .dash-card {
    background-color: #fff;
    width: 100%;
    border-radius: 8px;
    margin: 16px;
    position: relative;
    .img-b {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 172px;
    }
  }
  .card-heading {
    background-color: #fff;
    border-bottom: solid 2px #f9f9f9;
    width: 100%;
    padding: 12px;
    display: grid;
    grid-template-columns: 2fr 3fr 1fr;
    margin-bottom: 12px;
    min-height: 75px;
  }
  .card-title {
    color: #0f3f44;
    font-family: 'Cooper Md BT', serif;
    font-size: 16px;
    align-self: center;
  }
  .top-five-item {
    background-color: #859a9d;
    display: grid;
    grid-template-columns: 8fr 2fr;
    margin: 12px 0;
    border-radius: 8px;
    padding: 12px;
    p,
    .top-five-text,
    .top-five-count {
      color: #fff;
    }
    p {
      margin: 0 !important;
      color: #fff !important;
      font-size: 12px !important;
    }
    .top-five-text {
      font-family: 'Cooper Md BT', serif;
      font-size: 20px;
      text-transform: capitalize;
    }
    .top-five-count {
      color: #fff;
      font-size: 24px;
      align-content: center;
      align-self: center;
      align-items: center;
      margin-bottom: 8px;
      font-weight: 700;
      justify-self: center;
    }
  }
  label {
    margin-left: 16px;
    color: #26484e;
  }
  select {
    padding: 8px 12px 8px 32px;
    border-radius: 8px;
    margin-left: 8px;
  }
  .analytics-loader-show {
    background-color: var(--white-color);
    width: 100%;
    padding: 0px;
    position: relative;
    top: 0px;
    z-index: 9999;
    width: 100%;
    height: 90vh;
    padding-right: 0px;
    margin-right: -36px;
    padding-left: 0px;
  }
  .analytics-loader-hide {
    position: fixed;
    left: 9999px;
  }
  .k-label {
    margin-left: 0;
  }
  .k-input {
    font-family: 'Sofia Pro', sans-serif;
  }
  .k-button {
    background: #0f3f44;
    border-color: #0f3f44;
  }

  .graph-container {
    height: 500px;
  }
  //.minicards-dashboard-container {
  //  display: flex;
  //  width: 100%;
  //  /* padding: 10px; */
  //  /* margin-top: 10px; */
  //  /* margin-bottom: 10px; */
  //  margin-bottom: 50px;
  //  justify-content: space-between;
  //  align-items: flex-start;
  //}
  //.minicard-resume {
  //  display: flex;
  //  /* padding: 10px; */
  //  flex-direction: column;
  //  font-size: 1.7rem;
  //  width: 33.333%;
  //  align-items: center;
  //  justify-content: center;
  //}
  //.mcd-text-container {
  //  width: 100%;
  //  display: flex;
  //  flex-direction: row;
  //  justify-content: space-evenly;
  //  margin-top: auto;
  //}
  //.minicard-d-title {
  //  font-size: 1rem;
  //  width: 100%;
  //  text-align: center;
  //  color: #aeaec1;
  //  font-weight: 500;
  //}
  //.minicard-d-result {
  //  font-size: 1.4rem;
  //  color: #aeaec1;
  //  font-weight: 500;
  //  line-height: 32px;
  //  width: 100%;
  //  text-align: center;
  //  margin-bottom: 4px;
  //}
  //.decorative-icon-a {
  //  font-size: 2.4rem !important;
  //  color: #aeaec1;
  //  display: flex;
  //  align-self: flex-start;
  //  justify-content: center;
  //  margin-left: auto;
  //  margin-right: auto;
  //}
  //.graph-container {
  //  width: 100%;
  //  display: block;
  //  margin-left: auto;
  //  margin-right: auto;
  //  border-radius: 5px;
  //  padding: 10px;
  //  height: 500px;
  //}
  //.graph-filter-select {
  //  width: auto;
  //  font-size: 1.35rem !important;
  //  display: inline-block;
  //  margin-left: 10px;
  //  margin-right: 30px;
  //  background-color: var(--white-color);
  //  border-radius: 4px;
  //  text-align: center;
  //  padding-bottom: 2px;
  //}
  //.graph-filter-label {
  //  width: auto;
  //  font-size: 1.2rem !important;
  //  display: inline-block;
  //  margin-left: 10px;
  //}
  //.graph-filter-container {
  //  width: auto;
  //  display: flex;
  //  align-items: center;
  //  justify-content: flex-end;
  //  margin-left: auto;
  //  padding-top: 8px;
  //}
  //.graph-left-container {
  //  width: 47.5%;
  //  display: inline-block;
  //}
  //.graph-left-container-users {
  //  width: 100%;
  //  display: inline-block;
  //  margin-left: auto;
  //  margin-right: auto;
  //}
  //.graph-right-container {
  //  width: 45%;
  //  align-self: flex-start;
  //  display: inline-flex;
  //  float: right;
  //  margin-top: 10px;
  //  padding: 20px 12px;
  //  background-color: var(--dashboard);
  //  border-radius: 8px;
  //}
  //.top-five-icon {
  //  font-size: 3rem;
  //  color: lightseagreen;
  //  display: inline-block;
  //  width: 20%;
  //}
  //.minicard-left-top {
  //  width: 100%;
  //}
  //.top-five-title {
  //  width: 100%;
  //  font-size: 1.4rem;
  //  margin-bottom: 40px;
  //  display: inline-block;
  //  align-self: flex-start;
  //  font-weight: 600;
  //}
  //.top-five-item {
  //  width: 100%;
  //  padding: 12px 16px;
  //  display: flex;
  //  margin-bottom: 20px;
  //  font-size: 1.6rem;
  //  border-radius: 4px;
  //  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  //  background-color: white;
  //  justify-content: space-between;
  //}
  //.top-five-text {
  //  font-size: 1.6rem;
  //  line-height: 32px;
  //  font-weight: 500;
  //  display: block;
  //  margin: 0px;
  //  font-weight: 800;
  //}
  //.top-five-score {
  //  font-size: 1.2rem;
  //  background-color: mediumblue;
  //  color: white;
  //  border-radius: 50%;
  //  padding-left: 7px;
  //  padding-right: 7px;
  //  padding-bottom: 3.5px;
  //  padding-top: 3.5px;
  //  margin-left: 8px;
  //}
  //.graph-table-container {
  //  width: 100%;
  //  display: block;
  //}
  //.analytics-container-hide {
  //  width: 100%;
  //  border: none;
  //  position: fixed;
  //  left: 9999px;
  //}

  //.graph-table-title {
  //  font-size: 1.2rem;
  //  margin-left: 40px;
  //  margin-top: 20px;
  //  margin-bottom: -40px;
  //}
  //.graph-full-container-vacancy {
  //  flex-direction: row;
  //  width: 100%;
  //  display: flex;
  //  flex-wrap: wrap;
  //  justify-content: space-between;
  //}
  //.date-filters-container {
  //  width: 100%;
  //  display: flex;
  //  flex-direction: row;
  //  justify-content: space-evenly;
  //  align-items: center;
  //}
  //.date-main-filters-container {
  //  width: 100%;
  //  display: flex;
  //  flex-direction: column;
  //  justify-content: center;
  //  align-items: center;
  //}
  //.date-picker-element {
  //  width: 100%;
  //  display: block;
  //  padding: 4px;
  //  font-size: 1.3rem;
  //  text-align: center;
  //}
  //.button-date-filter {
  //  font-size: 1.3rem;
  //  border: solid 1px;
  //  padding: 3px 14px;
  //  margin-top: 7px;
  //  margin-bottom: 7px;
  //  background-color: #5373fc;
  //  color: var(--white-color);
  //}
  //.react-datepicker {
  //  font-size: 1.2rem;
  //}
  //.mt-1 {
  //  margin-top: 10px;
  //}
  //.analytics-title {
  //  font-size: 1.4rem;
  //  text-align: center;
  //  margin-bottom: -15px;
  //  margin-top: 15px;
  //  font-weight: 800;
  //}
  //.dashboard-navbar {
  //  width: 100%;
  //  display: flex;
  //  justify-content: space-between;
  //  align-items: center;
  //  margin-bottom: 42px;
  //}
  //.dashboard-title {
  //  font-size: 2rem;
  //  font-weight: 500;
  //  line-height: 32px;
  //  width: auto;
  //  display: block;
  //  color: var(--text-black);
  //  margin: 0px;
  //}
  //.dashboard-date-box {
  //  border: solid 2px #edeef9;
  //  padding: 4px;
  //}
  //.dashboard-options {
  //  display: flex;
  //  align-items: center;
  //  justify-content: space-between;
  //}
  //.dashboard-export {
  //  font-size: 1.6rem !important;
  //  text-align: center;
  //  border-radius: 8px;
  //  color: var(--text-black);
  //  height: 43px;
  //  width: 173px;
  //  padding: 8px;
  //  margin: 0px;
  //  background-color: transparent;
  //  font-weight: 500;
  //  display: flex;
  //  justify-content: space-around;
  //  cursor: pointer;
  //}
  //.dashboard-export-blue {
  //  background-color: #5373fc;
  //  color: white;
  //}
  //.dashboard-date-container {
  //  display: flex;
  //  justify-content: center;
  //  align-items: center;
  //}
  //.analytics-container {
  //  width: 100%;
  //  border: none;
  //  position: relative;
  //}
  //.dashboard-card {
  //  width: 100%;
  //  padding: 0;
  //  display: flex;
  //  flex-wrap: wrap;
  //  flex-direction: row;
  //  margin: 0;
  //  justify-content: space-around;
  //}
  //.dashboard-date-picker input {
  //  font-size: 1.4rem !important;
  //}
  //.minicards-container {
  //  width: 35%;
  //  display: flex;
  //  flex-wrap: wrap;
  //  align-items: flex-start;
  //  justify-content: flex-start;
  //  margin-top: 32px;
  //}
  //.vacancy-graph-container {
  //  width: 60%;
  //}
`;
